import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";

import {
  useSsoRequestTokenMutation,
  useSsoTokenExchangeMutation
} from "@/api/auth/useAuthAPI";
import LoadingPage from "@/components/ui/LoadingPage";
import useSsoLogin from "@/hooks/auth/useSsoLogin";
import { setAuth } from "@/store/slice/authSlice";
import { SsoAuthorizeErrorInfo } from "@/types/auth";
import { CustomError } from "@/utils/errors";
import { getStoredCodeVerifier, getStoredState } from "@/utils/pkceUtils";
import {
  clearSsoParams,
  getBasePath,
  getSsoCallbackUrl,
  getStoredProviderConfig,
  isSameOriginUrl
} from "@/utils/sso";

/**
 * Normalise a failure into OAuth-style `error` / `error_description`. The two
 * hops of the flow fail differently, and the API layer hands each to us in its
 * own way:
 *
 * - IdP token endpoint: not our API, so its raw OAuth body
 *   (`{ error: "invalid_grant", error_description }`) is kept on
 *   `CustomError.details`.
 * - BFF token exchange: our own API, so the standard handling applies — the
 *   envelope reason lands on `CustomError.reason` and the display message on
 *   `message`.
 *
 * Everything else (non-CustomError, empty body) falls back to a generic code.
 */
export const toOAuthError = (error: unknown): SsoAuthorizeErrorInfo => {
  if (!(error instanceof CustomError)) {
    return { error: "sso_failed" };
  }

  const details = error.details as
    | { error?: string; error_description?: string }
    | null
    | undefined;

  // IdP OAuth body: forward `error` / `error_description` as-is.
  if (typeof details?.error === "string") {
    return {
      error: details.error,
      errorDescription: details.error_description
    };
  }

  // BFF failure: the reason code is the error, its resolved message the detail.
  if (error.reason) {
    return { error: error.reason, errorDescription: error.message };
  }

  return { error: "sso_failed", errorDescription: error.message };
};

/**
 * LOKASI SSO redirect landing. The IdP sends the browser back here with
 * `?code&state` once the user is authenticated. The whole exchange completes on
 * this origin — which is what makes the per-origin `sessionStorage` verifier
 * work:
 *
 * 1. validate `state` against the value we stored (CSRF), before any network call,
 * 2. redeem `code` + our PKCE verifier at the IdP token endpoint,
 * 3. relay the resulting `id_token` to our own BFF, which sets the app session
 *    cookie (`token_exchange`),
 * 4. mark the session as established and return the user where they were.
 *
 * Nothing is stored in JS: the cookie is the truth, `hasLoggedIn` only tells the
 * app it is worth calling `/v1/auth/status`.
 */
const LokasiSsoCallbackPage = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const hasRun = useRef(false);

  const { mutateAsync: requestSsoToken } = useSsoRequestTokenMutation();
  const { mutateAsync: exchangeSsoToken } = useSsoTokenExchangeMutation();
  const { redirectToAuthLogin, fetchSsoProvider } = useSsoLogin();

  const code = searchParams.get("code");
  const returnedState = searchParams.get("state");
  const redirectTo = searchParams.get("redirect_to");

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    // On failure, bounce to the auth app login with a fresh set of authorize
    // params so the user can retry. `errorInfo` is forwarded only for real IdP /
    // BFF errors; precondition failures retry silently. If even the redirect
    // can't be built (provider lookup down), fall back to the password login.
    const handleFailure = async (errorInfo?: SsoAuthorizeErrorInfo) => {
      clearSsoParams();

      const target =
        redirectTo ?? `${globalThis.location.origin}${getBasePath()}/`;

      try {
        await redirectToAuthLogin(target, errorInfo);
      } catch {
        globalThis.location.replace(
          `${globalThis.location.origin}${getBasePath()}/login`
        );
      }
    };

    const complete = async () => {
      const codeVerifier = getStoredCodeVerifier();
      const storedState = getStoredState();

      // Need the code, our stored PKCE verifier, and a matching state (CSRF).
      // A mismatch aborts *before* the code is redeemed.
      if (
        !code ||
        !codeVerifier ||
        !returnedState ||
        returnedState !== storedState
      ) {
        // Client-side precondition failure (stale tab, direct hit) — retry
        // silently, there is no user-facing error worth showing.
        await handleFailure();
        return;
      }

      try {
        // Reuse the provider config cached at startSso so the short-lived code
        // is redeemed immediately; only re-fetch if it is somehow missing.
        const provider =
          getStoredProviderConfig() ?? (await fetchSsoProvider());

        const tokens = await requestSsoToken({
          url: provider.auth_token_url,
          payload: {
            // Must match what was sent to /authorize: client_id from the
            // provider config, and this callback's own URL as redirect_uri.
            client_id: provider.client_id,
            redirect_uri: getSsoCallbackUrl(),
            code,
            code_verifier: codeVerifier,
            grant_type: "authorization_code"
          }
        });

        // Relay the id_token to our BFF, which sets the app session cookie.
        await exchangeSsoToken({
          idToken: tokens.id_token,
          tenantId: provider.tenant_id
        });

        dispatch(setAuth({ hasLoggedIn: true }));
        clearSsoParams();

        // Return the user to where they were — same-origin only, else the app
        // root. `redirect_to` comes from the URL, so it is untrusted input.
        // A full navigation (not `navigate`) is deliberate: it re-bootstraps the
        // app with the session cookie in place.
        globalThis.location.replace(
          redirectTo && isSameOriginUrl(redirectTo)
            ? redirectTo
            : `${globalThis.location.origin}${getBasePath()}/`
        );
      } catch (error) {
        console.error("[SSO] token exchange failed", error);
        await handleFailure(toOAuthError(error));
      }
    };

    complete();
    // Run once for the code we landed with.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Either completing the exchange or (on failure) redirecting to the auth app
  // login — both end in a navigation away, so just show a loader.
  return <LoadingPage />;
};

export default LokasiSsoCallbackPage;
